import { cn } from "@/lib/utils";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { OrderWithRelations } from "./types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { User, Wrench, Truck } from "lucide-react";
import { getOrderCardClass } from "./orderCardUtils";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { Location } from "@/types/tms";
import { formatTimeWithTz, formatWindow } from "@/lib/order-time";
import { resolveOrderEndpoints } from "@/lib/order-endpoints";

interface ScheduleOrderCellProps {
  order: OrderWithRelations;
  onClick?: () => void;
  locations?: Location[];
}

export function ScheduleOrderCell({ order, onClick, locations }: ScheduleOrderCellProps) {
  const viewerTz = useViewerTimezone();
  const isWorkOrder = order.is_work_order;

  // Canonical short_code resolution shared across every order surface.
  const { from, to } = resolveOrderEndpoints(order, locations);
  const shipFromCode = from.label;
  const shipToCode = to.label;

  const getCardClass = () => {
    if (isWorkOrder) return "order-card-work-order";
    return getOrderCardClass(order.status);
  };
  
  const showMissingIcons = !isWorkOrder && order.status !== "canceled" && order.status !== "delivered";

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div
            onClick={onClick}
            className={cn(
              "w-full min-w-0 min-h-16 p-2 rounded cursor-pointer hover:ring-2 hover:ring-primary/50 transition-shadow flex flex-col",
              getCardClass()
            )}
          >
            <div className="flex items-center gap-1 text-xs font-medium truncate">
              {isWorkOrder && <Wrench className="h-3 w-3 shrink-0 text-[hsl(var(--status-in-transit))]" />}
              <span className="truncate">{order.unique_id}</span>
              {showMissingIcons && (
                <div className="flex items-center gap-0.5 ml-auto shrink-0">
                  {!order.assigned_tractor_id && (
                    <Truck className="h-3 w-3 text-destructive" />
                  )}
                  {!order.assigned_operator_id && (
                    <User className="h-3 w-3 text-destructive" />
                  )}
                </div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground truncate mt-0.5">
              {isWorkOrder ? order.work_order_description : `→ ${shipToCode}`}
            </div>
            <div className="text-[10px] text-muted-foreground mt-auto">
              {formatWindow(order.ready_time, order.ready_time_end, viewerTz, "HH:mm")}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1.5 text-xs">
            <div className="font-semibold flex items-center gap-1.5">
              {isWorkOrder && <Wrench className="h-3 w-3 text-[hsl(var(--status-in-transit))]" />}
              {order.unique_id}
              {isWorkOrder ? (
                <span className="text-[hsl(var(--status-in-transit))]">(Work Order)</span>
              ) : (
                <OrderStatusBadge status={order.status} />
              )}
            </div>
            {isWorkOrder ? (
              <>
                {order.work_order_description && (
                  <div className="text-muted-foreground">
                    {order.work_order_description}
                  </div>
                )}
                <div className="text-muted-foreground">
                  Return to: {shipToCode}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground">
                {shipFromCode} → {shipToCode}
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1 border-t">
              <div>
                <span className="text-muted-foreground">{isWorkOrder ? "Start:" : "Ready:"}</span>{" "}
                {isWorkOrder
                  ? formatTimeWithTz(order.ready_time, viewerTz, "MMM d, HH:mm")
                  : formatWindow(order.ready_time, order.ready_time_end, viewerTz, "MMM d, HH:mm")}
              </div>
              <div>
                <span className="text-muted-foreground">{isWorkOrder ? "End:" : "Delivery:"}</span>{" "}
                {isWorkOrder
                  ? formatTimeWithTz(order.requested_delivery_time, viewerTz, "MMM d, HH:mm")
                  : formatWindow(order.requested_delivery_time, order.requested_delivery_time_end, viewerTz, "MMM d, HH:mm")}
              </div>
            </div>
            {!isWorkOrder && order.assigned_operator && (
              <div className="pt-1 border-t">
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>{order.assigned_operator.name}</span>
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
